import React from "react";
import {
  Box,
  Button,
  Container,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import PhoneIcon from "@mui/icons-material/Phone";
import LockIcon from "@mui/icons-material/Lock";
import { useState } from "react";
import { setUserCredentials } from "../../redux/user/userSlice";
import toast from "react-hot-toast";
import { editProfile } from "../../services/user/user";
import { useTranslation } from "react-i18next";

function Profile() {
  const { phone, type } = useSelector((state) => state.user);
  const [currentPass, setCurrentPassword] = useState("");
  const [newPass, setNewPassword] = useState("");
  const [repeatPass, setRepeatPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(phone || ""); // Initialize with the value from Redux state
  const [error, setError] = useState(null); // For general error handling
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  // Function to handle form submission
  const handleEditInfo = async (e) => {
    e.preventDefault();

    // Form validation: Check if passwords match
    if (newPass !== repeatPass) {
      toast.error(t("Profile.passwordMismatch"));
      return;
    }

    const data = {
      phone: phoneNumber,
      oldpass: currentPass,
      newpass: newPass,
      repeatpass: repeatPass,
    };

    try {
      // Try to update profile
      await editProfile(data);
      dispatch(
        setUserCredentials({ phone: phoneNumber, newpassword: newPass })
      );
      toast.success(t("Profile.updateSuccess"), { duration: 5000 });

      // Reset form values on successful submission
      setCurrentPassword("");
      setNewPassword("");
      setRepeatPassword("");
      setError(null);
    } catch (err) {
      // Handle error: Display user-friendly error message
      setError(err.response?.data || t("Profile.updateError"));
      toast.error(err.response?.data || t("Profile.updateError"), {
        duration: 5000,
      });
    }
  };

  return (
    <Container
      sx={{
        justifyContent: "center",
        display: "flex",
        alignItems: "center",
        padding: "1rem",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        component="form"
        onSubmit={handleEditInfo}
        sx={{
          backgroundColor: "#F4F2FB",
          padding: "1rem",
          borderRadius: "10px",
          color: "black",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
          marginTop: "2rem",
        }}
      >
        <Typography
          variant="h3"
          component="h5"
          align="center"
          sx={{ marginBottom: "1.5rem", color: "#3b3b3b" }}
        >
          {t("Profile.title")}
        </Typography>
        <TextField
          onChange={(e) => setPhoneNumber(e.target.value)} // Set the value to the updated state
          id="number"
          label={t("Profile.phone")}
          value={phoneNumber} // Use phoneNumber as the value
          variant="outlined"
          fullWidth
          sx={{ marginBottom: "1.75rem" }}
          error={!!error}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon />
              </InputAdornment>
            ),
            inputMode: "numeric", // Hint for numeric keyboard on mobile
            pattern: "[0-9]*",
          }}
        />
        <TextField
          onChange={(e) => setCurrentPassword(e.target.value)}
          id="current-pass"
          label={t("Profile.currentPassword")}
          variant="outlined"
          sx={{ marginBottom: "1.75rem" }}
          fullWidth
          error={!!error}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          onChange={(e) => setNewPassword(e.target.value)}
          id="new-pass"
          label={t("Profile.newPassword")}
          variant="outlined"
          sx={{ marginBottom: "1.75rem" }}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          type="password"
          onChange={(e) => setRepeatPassword(e.target.value)}
          id="repeat-pass"
          label={t("Profile.repeatPassword")}
          variant="outlined"
          fullWidth
          sx={{ marginBottom: "2.25rem" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
          }}
        />

        <Button
          disabled={newPass !== repeatPass}
          type="submit"
          variant="contained"
          color="secondary"
          sx={{
            width: "100%",
            borderRadius: "100px",
            marginBottom: "1rem",
            padding: "12px",
            fontSize: "16px",
          }}
          size="large"
        >
          {t("Profile.update")}
        </Button>
      </Box>
    </Container>
  );
}

export default Profile;
